import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { authApi, setApiTokens } from '../api/client';
import { tokens } from '../theme/tokens';
import './Login.css';

interface LoginProps {
  onLogin: (token: string, user: any) => void;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  // Check for invite code in URL
  const urlParams = new URLSearchParams(window.location.search);
  const inviteCode = urlParams.get('invite') || urlParams.get('code') || '';

  const [mode, setMode] = useState<'signin' | 'register'>(inviteCode ? 'register' : 'signin');
  const [registerOption, setRegisterOption] = useState<'join' | 'create' | null>(inviteCode ? 'join' : null);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [tenantCode, setTenantCode] = useState(inviteCode);
  const [tenantName, setTenantName] = useState('');
  const [lookupResult, setLookupResult] = useState<{ name: string } | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Google post-auth states
  const [googlePending, setGooglePending] = useState<{ googleEmail: string; googleId: string } | null>(null);
  const [googleTenantOption, setGoogleTenantOption] = useState<'join' | 'create' | null>(null);
  const [googleTenantCode, setGoogleTenantCode] = useState('');
  const [googleTenantName, setGoogleTenantName] = useState('');
  const [googleLookupResult, setGoogleLookupResult] = useState<{ name: string } | null>(null);

  // Multi-tenant picker (flow 3)
  const [tenantPicker, setTenantPicker] = useState<Array<{ id: string; name: string; slug: string; userId: string }> | null>(null);

  useEffect(() => {
    const blocked = localStorage.getItem('tenant_blocked_reason');
    if (blocked) {
      setError(blocked);
      localStorage.removeItem('tenant_blocked_reason');
    }
    // Check if returning from Supabase OAuth
    const pendingEmail = localStorage.getItem('supabase_pending_email');
    const pendingId = localStorage.getItem('supabase_pending_id');
    const pendingTenants = localStorage.getItem('supabase_pending_tenants');
    const pendingPicker = localStorage.getItem('supabase_pending_picker');

    if (pendingEmail) {
      const tenants = pendingTenants ? JSON.parse(pendingTenants) : [];
      if (pendingPicker && tenants.length > 1) {
        // Flow 3: multiple tenants — show picker
        setTenantPicker(tenants);
        setGooglePending({ googleEmail: pendingEmail, googleId: pendingId || '' });
      } else {
        // Flow 1: no tenant — show create/join
        setGooglePending({ googleEmail: pendingEmail, googleId: pendingId || '' });
      }
      localStorage.removeItem('supabase_pending_email');
      localStorage.removeItem('supabase_pending_id');
      localStorage.removeItem('supabase_pending_tenants');
      localStorage.removeItem('supabase_pending_picker');
    }
  }, []);

  // Real-time tenant code lookup
  useEffect(() => {
    const code = tenantCode.trim();
    if (code.length < 3) { setLookupResult(null); return; }
    const timeout = setTimeout(async () => {
      try {
        const res = await authApi.lookupTenant(code);
        setLookupResult(res.data.success && res.data.name ? { name: res.data.name } : null);
      } catch { setLookupResult(null); }
    }, 300);
    return () => clearTimeout(timeout);
  }, [tenantCode]);

  useEffect(() => {
    const code = googleTenantCode.trim();
    if (code.length < 3) { setGoogleLookupResult(null); return; }
    const timeout = setTimeout(async () => {
      try {
        const res = await authApi.lookupTenant(code);
        setGoogleLookupResult(res.data.success && res.data.name ? { name: res.data.name } : null);
      } catch { setGoogleLookupResult(null); }
    }, 300);
    return () => clearTimeout(timeout);
  }, [googleTenantCode]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const res = await authApi.login(email, password);
      setApiTokens({ accessToken: res.data.accessToken });
      localStorage.setItem('token', res.data.accessToken);
      onLogin(res.data.accessToken, res.data.user);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!password || password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      const res = await authApi.register({
        email, password, firstName, lastName,
        tenantCode: registerOption === 'join' ? tenantCode : undefined,
        tenantName: registerOption === 'create' ? tenantName : undefined,
      });
      setApiTokens({ accessToken: res.data.accessToken });
      localStorage.setItem('token', res.data.accessToken);
      onLogin(res.data.accessToken, res.data.user);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleTenantSelect = async (tenantId: string) => {
    setError(null);
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        setError('Session expired. Please sign in again.');
        setTenantPicker(null);
        setGooglePending(null);
        setLoading(false);
        return;
      }
      const res = await authApi.supabaseExchange(session.access_token, tenantId);
      if (res.data.accessToken) {
        setApiTokens({ accessToken: res.data.accessToken });
        localStorage.setItem('token', res.data.accessToken);
        onLogin(res.data.accessToken, res.data.user);
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to select organization');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setLoading(true);
    // Preserve invite code across OAuth redirect
    if (inviteCode) {
      localStorage.setItem('oauth_invite_code', inviteCode);
    }
    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin,
      },
    });
    if (oauthError) {
      setError(oauthError.message || 'Google sign-in failed');
      setLoading(false);
    }
    // Browser will redirect — loading state stays until navigation
  };

  const handleGoogleTenantSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!googlePending) return;
    setError(null);
    setLoading(true);
    try {
      const code = googleTenantOption === 'join' ? googleTenantCode : undefined;
      const name = googleTenantOption === 'create' ? googleTenantName : undefined;
      const res = await authApi.register({
        email: googlePending.googleEmail,
        password: Math.random().toString(36) + 'Aa1!',
        firstName: '',
        lastName: '',
        tenantCode: code,
        tenantName: name,
      });
      setApiTokens({ accessToken: res.data.accessToken });
      localStorage.setItem('token', res.data.accessToken);
      onLogin(res.data.accessToken, res.data.user);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Setup failed');
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (newMode: 'signin' | 'register') => {
    setMode(newMode);
    setError(null);
    setRegisterOption(null);
    setTenantCode('');
    setTenantName('');
    setLookupResult(null);
  };

  // ─── Tenant choice UI (shared between register and google post-auth) ───

  const renderTenantChoice = (
    option: 'join' | 'create' | null,
    setOption: (v: 'join' | 'create' | null) => void,
    code: string,
    setCode: (v: string) => void,
    name: string,
    setName: (v: string) => void,
    lookup: { name: string } | null,
  ) => {
    if (!option) {
      return (
        <div className="login-tenant-choice">
          <button type="button" className="login-tenant-btn create" onClick={() => setOption('create')}>
            <div className="login-tenant-btn-icon create">&#x1F3E2;</div>
            <span className="login-tenant-btn-label">Create New Org</span>
            <span className="login-tenant-btn-desc">Start your organization</span>
          </button>
          <button type="button" className="login-tenant-btn join" onClick={() => setOption('join')}>
            <div className="login-tenant-btn-icon join">&#x1F465;</div>
            <span className="login-tenant-btn-label">Join Existing</span>
            <span className="login-tenant-btn-desc">Use an invite code</span>
          </button>
        </div>
      );
    }

    if (option === 'create') {
      return (
        <div className="login-field">
          <div className="login-tenant-header">
            <span className="create">Creating a new organization</span>
            <button type="button" onClick={() => { setOption(null); setName(''); }}>Change</button>
          </div>
          <input
            type="text"
            className="login-input"
            placeholder="e.g. Acme Corp"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
      );
    }

    return (
      <div className="login-field">
        <div className="login-tenant-header">
          <span className="join">Joining with a code</span>
          <button type="button" onClick={() => { setOption(null); setCode(''); }}>Change</button>
        </div>
        <input
          type="text"
          className="login-input"
          placeholder="e.g. ACME-2026-XY1Z"
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
        {lookup && (
          <div className="login-lookup-found">
            <p>Joining: <span>{lookup.name}</span></p>
          </div>
        )}
      </div>
    );
  };

  // ─── Flow 3: Tenant picker (multiple tenants) ───

  if (tenantPicker && tenantPicker.length > 1 && googlePending) {
    return (
      <div className="login-page">
        <div className="login-hero">
          <div className="login-hero-grid" />
          <div className="login-hero-content">
            <img src="/NexusLogo.png" alt="Nexus" style={{ height: 80 }} />
            <div className="login-hero-features">
              <div className="login-hero-feature">
                <div className="login-hero-feature-dot" />
                <span>Unified view of engineering health and velocity</span>
              </div>
              <div className="login-hero-feature">
                <div className="login-hero-feature-dot" />
                <span>Org chart, roles, and team management</span>
              </div>
              <div className="login-hero-feature">
                <div className="login-hero-feature-dot" />
                <span>Jira, Jellyfish, and Confluence integrations</span>
              </div>
            </div>
          </div>
        </div>

        <div className="login-form-side">
          <div className="login-form-wrapper">
            <div className="login-mobile-brand">
              <img src="/NexusLogo.png" alt="Nexus" style={{ height: 48 }} />
            </div>

            <div className="login-desktop-heading">
              <h2>Choose an organization</h2>
              <p>You belong to multiple organizations</p>
            </div>

            <div className="login-card">
              {error && (
                <div className="login-error">
                  <svg className="login-error-icon" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                  </svg>
                  <p>{error}</p>
                </div>
              )}

              <p style={{ fontSize: 14, color: tokens.color.textSecondary, textAlign: 'center', marginBottom: 16, marginTop: 0 }}>
                Signed in as {googlePending.googleEmail}
              </p>

              <div className="login-form" style={{ gap: 8 }}>
                {tenantPicker.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    className="login-tenant-picker-btn"
                    onClick={() => handleTenantSelect(t.id)}
                    disabled={loading}
                  >
                    <div className="login-tenant-picker-icon">
                      {t.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, color: tokens.color.text, fontSize: 14 }}>{t.name}</div>
                      <div style={{ fontSize: 12, color: tokens.color.textSecondary }}>{t.slug}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Flow 1: Google post-auth — no tenant (create or join) ───

  if (googlePending) {
    return (
      <div className="login-page">
        <div className="login-hero">
          <div className="login-hero-grid" />
          <div className="login-hero-content">
            <img src="/NexusLogo.png" alt="Nexus" style={{ height: 80 }} />
            <div className="login-hero-features">
              <div className="login-hero-feature">
                <div className="login-hero-feature-dot" />
                <span>Unified view of engineering health and velocity</span>
              </div>
              <div className="login-hero-feature">
                <div className="login-hero-feature-dot" />
                <span>Org chart, roles, and team management</span>
              </div>
              <div className="login-hero-feature">
                <div className="login-hero-feature-dot" />
                <span>Jira, Jellyfish, and Confluence integrations</span>
              </div>
            </div>
          </div>
        </div>

        <div className="login-form-side">
          <div className="login-form-wrapper">
            <div className="login-mobile-brand">
              <img src="/NexusLogo.png" alt="Nexus" style={{ height: 48 }} />
            </div>

            <div className="login-desktop-heading">
              <h2>One more step</h2>
              <p>Join or create an organization to get started</p>
            </div>

            <div className="login-card">
              {error && (
                <div className="login-error">
                  <svg className="login-error-icon" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                  </svg>
                  <p>{error}</p>
                </div>
              )}

              <p style={{ fontSize: 14, color: tokens.color.textSecondary, textAlign: 'center', marginBottom: 16, marginTop: 0 }}>
                Welcome, {googlePending.googleEmail}!
              </p>

              {renderTenantChoice(
                googleTenantOption, setGoogleTenantOption,
                googleTenantCode, setGoogleTenantCode,
                googleTenantName, setGoogleTenantName,
                googleLookupResult,
              )}

              {googleTenantOption && (
                <form onSubmit={handleGoogleTenantSubmit} style={{ marginTop: 16 }}>
                  <button
                    type="submit"
                    disabled={loading}
                    className={`login-submit ${googleTenantOption === 'join' ? 'green' : ''}`}
                  >
                    {loading && <span className="login-spinner" />}
                    {loading ? 'Setting up...' : 'Continue'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Main login / register ───

  return (
    <div className="login-page">
      {/* Left decorative panel */}
      <div className="login-hero">
        <div className="login-hero-grid" />
        <div className="login-hero-content">
          <img src="/NexusLogo.png" alt="Nexus" style={{ height: 80 }} />
          <div className="login-hero-features">
            <div className="login-hero-feature">
              <div className="login-hero-feature-dot" />
              <span>Unified view of engineering health and velocity</span>
            </div>
            <div className="login-hero-feature">
              <div className="login-hero-feature-dot" />
              <span>Org chart, roles, and team management</span>
            </div>
            <div className="login-hero-feature">
              <div className="login-hero-feature-dot" />
              <span>Jira, Jellyfish, and Confluence integrations</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right side: form */}
      <div className="login-form-side">
        <div className="login-form-wrapper">
          {/* Mobile branding */}
          <div className="login-mobile-brand">
            <img src="/NexusLogo.png" alt="Nexus" style={{ height: 48 }} />
          </div>

          {/* Desktop heading */}
          <div className="login-desktop-heading">
            <h2>{mode === 'signin' ? 'Welcome back' : 'Create your account'}</h2>
            <p>{mode === 'signin' ? 'Sign in to access your dashboard' : 'Get started with your organization'}</p>
          </div>

          <div className="login-card">
            {/* Tab switcher */}
            <div className="login-tabs">
              <button
                className={`login-tab ${mode === 'signin' ? 'active' : ''}`}
                onClick={() => switchMode('signin')}
              >
                Sign In
              </button>
              <button
                className={`login-tab ${mode === 'register' ? 'active' : ''}`}
                onClick={() => switchMode('register')}
              >
                Register
              </button>
            </div>

            {/* Invite banner */}
            {inviteCode && lookupResult && (
              <div className="login-invite-banner">
                <div className="login-invite-banner-icon">&#x1F465;</div>
                <div>
                  <p className="login-invite-banner-title">
                    You've been invited to join <strong>{lookupResult.name}</strong>!
                  </p>
                  <p className="login-invite-banner-desc">
                    {mode === 'signin' ? 'Sign in to join your organization.' : 'Create an account to join.'}
                  </p>
                </div>
              </div>
            )}

            {/* Google sign-in — shown FIRST when invite link */}
            {inviteCode && (
              <>
                <button type="button" onClick={handleGoogleSignIn} disabled={loading} className="login-google-btn">
                  <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59a14.5 14.5 0 010-9.18l-7.98-6.19a24.01 24.01 0 000 21.56l7.98-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
                  Continue with Google
                </button>
                <div className="login-divider">
                  <div className="login-divider-line"><div /></div>
                  <div className="login-divider-text"><span>or {mode === 'signin' ? 'sign in' : 'register'} with email</span></div>
                </div>
              </>
            )}

            {/* Error */}
            {error && (
              <div className="login-error">
                <svg className="login-error-icon" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
                <p>{error}</p>
              </div>
            )}

            {/* Sign In */}
            {mode === 'signin' && (
              <form onSubmit={handleLogin} className="login-form">
                <div className="login-field">
                  <label>Email Address</label>
                  <input type="email" className="login-input" placeholder="you@example.com" value={email}
                    onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
                </div>
                <div className="login-field">
                  <label>Password</label>
                  <input type="password" className="login-input" placeholder="Enter your password" value={password}
                    onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" />
                </div>
                <button type="submit" disabled={loading} className="login-submit">
                  {loading && <span className="login-spinner" />}
                  {loading ? 'Signing in...' : 'Sign In'}
                </button>
              </form>
            )}

            {/* Register */}
            {mode === 'register' && (
              <form onSubmit={handleRegister} className="login-form">
                <div className="login-name-row">
                  <div className="login-field">
                    <label>First Name</label>
                    <input type="text" className="login-input" placeholder="John" value={firstName}
                      onChange={(e) => setFirstName(e.target.value)} />
                  </div>
                  <div className="login-field">
                    <label>Last Name</label>
                    <input type="text" className="login-input" placeholder="Doe" value={lastName}
                      onChange={(e) => setLastName(e.target.value)} />
                  </div>
                </div>
                <div className="login-field">
                  <label>Email Address</label>
                  <input type="email" className="login-input" placeholder="you@example.com" value={email}
                    onChange={(e) => setEmail(e.target.value)} required autoComplete="email" />
                </div>
                <div className="login-field">
                  <label>Password</label>
                  <input type="password" className="login-input" placeholder="Min. 6 characters" value={password}
                    onChange={(e) => setPassword(e.target.value)} required autoComplete="new-password" />
                </div>

                {/* Organization choice — hidden when invite code pre-fills it */}
                {!inviteCode && (
                  <div className="login-field">
                    <label>Organization</label>
                    {renderTenantChoice(
                      registerOption, setRegisterOption,
                      tenantCode, setTenantCode,
                      tenantName, setTenantName,
                      lookupResult,
                    )}
                  </div>
                )}

                {(inviteCode || registerOption) && (
                  <button type="submit" disabled={loading} className={`login-submit ${inviteCode ? 'green' : ''}`}>
                    {loading && <span className="login-spinner" />}
                    {loading ? 'Creating account...' : inviteCode ? `Join ${lookupResult?.name || 'Organization'}` : 'Create Account'}
                  </button>
                )}
              </form>
            )}

            {/* Google sign-in — shown LAST when no invite link */}
            {!inviteCode && (
              <>
                <div className="login-divider">
                  <div className="login-divider-line"><div /></div>
                  <div className="login-divider-text"><span>or</span></div>
                </div>
                <button type="button" onClick={handleGoogleSignIn} disabled={loading} className="login-google-btn">
                  <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59a14.5 14.5 0 010-9.18l-7.98-6.19a24.01 24.01 0 000 21.56l7.98-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/></svg>
                  Continue with Google
                </button>
              </>
            )}

            {/* Switch mode */}
            <p className="login-switch">
              {mode === 'signin' ? (
                <>Don't have an account? <button onClick={() => switchMode('register')}>Register</button></>
              ) : (
                <>Already have an account? <button onClick={() => switchMode('signin')}>Sign in</button></>
              )}
            </p>
          </div>

          <p className="login-footer">&copy; {new Date().getFullYear()} Cetana. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
